import { ArrowLeftIcon, CheckIcon } from "@heroicons/react/24/outline";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import { apiErrorMessage, apiFieldErrors } from "../services/api";
import { fetchBranches, fetchWorkingHours } from "../services/branchService";
import { newIdempotencyKey } from "../services/bookingService";
import { fetchNoticeTypes, previewNotice, sendNotice } from "../services/noticeService";
import { fetchProviders } from "../services/providerService";
import { fetchCatalogue } from "../services/serviceService";

const STEPS = ["type", "scope", "bookings", "message", "channels", "confirm"];
const DAYS = [0, 1, 2, 3, 4, 5, 6];
const LANGS = ["ar", "en"];

/** "2026-11-02T14:00" (datetime-local) ⇄ "2026-11-02 14:00" (API). */
const toApiLocal = (value) => (value ? value.replace("T", " ").slice(0, 16) : "");
const toInputLocal = (value) => (value ? value.replace(" ", "T").slice(0, 16) : "");
const csv = (value) => (value ? value.split(",").filter(Boolean) : []);

/** The scope the URL prefills (shortcuts from time off, closures, provider status, diary). */
const scopeFromParams = (params) => {
  const scope = {};
  [
    "staff_id",
    "date_from",
    "date_to",
    "delay_minutes",
    "from_branch_id",
    "branch_id",
    "starts_at",
    "ends_at",
  ].forEach((key) => {
    if (params.get(key)) scope[key] = params.get(key);
  });
  if (params.get("branch_ids")) scope.branch_ids = csv(params.get("branch_ids"));
  if (params.get("service_ids")) scope.service_ids = csv(params.get("service_ids"));
  if (params.get("hours")) {
    try {
      scope.hours = JSON.parse(params.get("hours"));
    } catch {
      /* ignore a malformed link */
    }
  }
  return scope;
};

/** The scope as the API wants it: only this type's fields, numbers as numbers. */
const scopeForApi = (type, scope) => {
  const fields = [...(type?.scope ?? []), ...(type?.type === "price_change" ? ["branch_ids"] : [])];
  const out = {};
  fields.forEach((field) => {
    const value = scope[field];
    if (
      value === undefined ||
      value === "" ||
      (Array.isArray(value) && value.length === 0 && field !== "hours")
    )
      return;
    if (field === "delay_minutes") out[field] = Number(value);
    else if (field === "hours") out[field] = value ?? [];
    else out[field] = value;
  });
  if (type?.type === "opening_hours_change" && !out.hours) out.hours = [];
  return out;
};

/**
 * "Notify affected customers": type → scope → affected bookings → message →
 * channels → confirm & send. The server finds the bookings (future,
 * pending/confirmed, the sender's branches) and re-checks them at send time;
 * no booking changes unless the customer chooses to.
 */
export default function NoticeWizard() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const idempotencyKey = useRef(newIdempotencyKey());

  const [types, setTypes] = useState([]);
  const [lookups, setLookups] = useState({ branches: [], providers: [], services: [] });
  const [step, setStep] = useState(0);
  const [typeKey, setTypeKey] = useState("");
  const [scope, setScope] = useState({});
  const [preview, setPreview] = useState(null);
  const [selected, setSelected] = useState([]);
  const [message, setMessage] = useState({ title_ar: "", title_en: "", body_ar: "", body_en: "" });
  const [lang, setLang] = useState(i18n.language === "ar" ? "ar" : "en");
  const [email, setEmail] = useState(false);
  const [showExpected, setShowExpected] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [loadError, setLoadError] = useState("");

  const type = useMemo(() => types.find((x) => x.type === typeKey), [types, typeKey]);

  const applyTemplate = (def) => {
    if (!def) return;
    setMessage({
      title_ar: def.template.ar.title,
      title_en: def.template.en.title,
      body_ar: def.template.ar.body,
      body_en: def.template.en.body,
    });
  };

  useEffect(() => {
    fetchNoticeTypes()
      .then((data) => {
        setTypes(data.types);
        const prefill = params.get("type");
        const def = data.types.find((x) => x.type === prefill);
        if (def) {
          setTypeKey(def.type);
          setScope(scopeFromParams(params));
          applyTemplate(def);
          setStep(1);
        }
      })
      .catch((err) => setLoadError(apiErrorMessage(err, t("notices.loadFailed"))));

    Promise.all([
      fetchBranches(1, 100).catch(() => ({ branches: [] })),
      fetchProviders({ perPage: 100 }).catch(() => ({ providers: [] })),
      fetchCatalogue({ perPage: 100 }).catch(() => ({ services: [] })),
    ]).then(([b, p, s]) =>
      setLookups({ branches: b.branches ?? [], providers: p.providers ?? [], services: s.services ?? [] })
    );
    // Prefill once, from the link that opened the wizard.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const chooseType = (def) => {
    if (def.type !== typeKey) {
      setTypeKey(def.type);
      setScope({});
      setPreview(null);
      setSelected([]);
      applyTemplate(def);
    }
    setStep(1);
  };

  const setField = (field, value) => {
    setScope((s) => ({ ...s, [field]: value }));
    setPreview(null);
  };

  const findBookings = async () => {
    setBusy(true);
    setError("");
    setFieldErrors({});
    try {
      const data = await previewNotice(typeKey, scopeForApi(type, scope));
      setPreview(data);
      setSelected(data.bookings.map((b) => b.id));
      setStep(2);
    } catch (err) {
      setFieldErrors(apiFieldErrors(err));
      setError(apiErrorMessage(err, t("notices.previewFailed")));
    } finally {
      setBusy(false);
    }
  };

  const send = async () => {
    setBusy(true);
    setError("");
    setFieldErrors({});
    try {
      const body = await sendNotice(
        {
          type: typeKey,
          scope: scopeForApi(type, scope),
          appointment_ids: selected,
          message,
          email,
          show_expected_start: showExpected,
        },
        idempotencyKey.current
      );
      navigate(`/notices/${body.data.notice.id}`, {
        replace: true,
        state: { sent: true, skipped: body.data.skipped },
      });
    } catch (err) {
      setFieldErrors(apiFieldErrors(err));
      setError(apiErrorMessage(err, t("notices.sendFailed")));
      // A refused send (4xx) is stored against its key; the corrected send
      // is a new request. A dropped connection keeps the key, so a retry
      // replays rather than sending twice.
      if (err?.response && err.response.status < 500) idempotencyKey.current = newIdempotencyKey();
    } finally {
      setBusy(false);
    }
  };

  const selectedBookings = (preview?.bookings ?? []).filter((b) => selected.includes(b.id));
  const reach = {
    in_app: selectedBookings.filter((b) => b.contact.in_app).length,
    push: selectedBookings.filter((b) => b.contact.push).length,
    email: selectedBookings.filter((b) => b.contact.email).length,
    none: selectedBookings.filter((b) => !b.contact.in_app && !(email && b.contact.email)).length,
  };
  const messageComplete = LANGS.every((l) => message[`title_${l}`].trim() && message[`body_${l}`].trim());
  const canNext = [Boolean(type), Boolean(preview), selected.length > 0, messageComplete, true, true][step];

  const insertToken = (token) =>
    setMessage((m) => ({
      ...m,
      [`body_${lang}`]: `${m[`body_${lang}`]}${m[`body_${lang}`].endsWith(" ") ? "" : " "}${token}`,
    }));

  const fieldError = (field) => fieldErrors[`scope.${field}`]?.[0] ?? fieldErrors[`scope.${field}.0`]?.[0];

  if (loadError) {
    return (
      <div className="mx-auto max-w-4xl">
        <div className="rounded-lg border border-danger-line bg-danger-wash px-3 py-2 text-sm text-danger-text">
          {loadError}
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <Link
            to="/notices"
            className="mb-1 inline-flex items-center gap-1 text-dense text-ink-2 hover:text-ink"
          >
            <ArrowLeftIcon className="h-4 w-4 rtl:rotate-180" aria-hidden="true" />
            {t("notices.title")}
          </Link>
          <h1 className="text-xl font-semibold">{t("notices.wizard.title")}</h1>
          <p className="text-dense text-ink-2">{t("notices.wizard.subtitle")}</p>
        </div>
      </div>

      <ol className="mb-5 flex flex-wrap gap-2" aria-label={t("notices.wizard.steps")}>
        {STEPS.map((key, index) => (
          <li key={key}>
            <button
              type="button"
              disabled={index > step}
              onClick={() => setStep(index)}
              aria-current={index === step ? "step" : undefined}
              className={`inline-flex h-8 items-center gap-1.5 rounded-full border px-3 text-dense ${
                index === step
                  ? "border-accent bg-accent-wash font-semibold text-accent-text"
                  : index < step
                    ? "border-line text-ink"
                    : "border-line text-ink-muted"
              }`}
            >
              {index < step ? <CheckIcon className="h-4 w-4" aria-hidden="true" /> : <span>{index + 1}</span>}
              {t(`notices.wizard.step.${key}`)}
            </button>
          </li>
        ))}
      </ol>

      {error && (
        <div
          className="mb-3 rounded-lg border border-danger-line bg-danger-wash px-3 py-2 text-sm text-danger-text"
          role="alert"
        >
          <p>{error}</p>
          {Object.keys(fieldErrors).length > 1 && (
            <ul className="mt-1 list-disc ps-5">
              {Object.entries(fieldErrors).map(([field, messages]) => (
                <li key={field}>{messages[0]}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div className="card p-5">
        {step === 0 && (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {types.map((def) => (
              <button
                key={def.type}
                type="button"
                onClick={() => chooseType(def)}
                className={`rounded-xl border p-4 text-start transition hover:border-accent ${
                  def.type === typeKey ? "border-accent bg-accent-wash" : "border-line"
                }`}
              >
                <p className="font-semibold">{def.label}</p>
                <p className="mt-1 text-caption text-ink-2">{t(`notices.typeHint.${def.type}`)}</p>
              </button>
            ))}
          </div>
        )}

        {step === 1 && type && (
          <ScopeForm
            type={type}
            scope={scope}
            lookups={lookups}
            setField={setField}
            fieldError={fieldError}
          />
        )}

        {step === 2 && preview && (
          <BookingsStep preview={preview} selected={selected} setSelected={setSelected} />
        )}

        {step === 3 && type && (
          <div>
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <div className="inline-flex rounded-lg border border-line p-0.5" role="tablist">
                {LANGS.map((l) => (
                  <button
                    key={l}
                    type="button"
                    role="tab"
                    aria-selected={lang === l}
                    onClick={() => setLang(l)}
                    className={`h-8 rounded-md px-4 text-dense ${lang === l ? "bg-accent-wash font-semibold text-accent-text" : "text-ink-2"}`}
                  >
                    {t(`notices.lang.${l}`)}
                  </button>
                ))}
              </div>
              <button
                type="button"
                className="btn-secondary h-8 px-3 text-dense"
                onClick={() => applyTemplate(type)}
              >
                {t("notices.message.reset")}
              </button>
            </div>
            <div dir={lang === "ar" ? "rtl" : "ltr"} lang={lang}>
              <label className="field-label" htmlFor="notice-title">
                {t("notices.message.subject")}
              </label>
              <input
                id="notice-title"
                className="field-input"
                maxLength={150}
                value={message[`title_${lang}`]}
                onChange={(e) => setMessage((m) => ({ ...m, [`title_${lang}`]: e.target.value }))}
              />
              {fieldErrors[`message.title_${lang}`] && (
                <p className="field-error">{fieldErrors[`message.title_${lang}`][0]}</p>
              )}
              <label className="field-label mt-3" htmlFor="notice-body">
                {t("notices.message.body")}
              </label>
              <textarea
                id="notice-body"
                rows={6}
                maxLength={1000}
                className="field-input"
                value={message[`body_${lang}`]}
                onChange={(e) => setMessage((m) => ({ ...m, [`body_${lang}`]: e.target.value }))}
              />
              {fieldErrors[`message.body_${lang}`] && (
                <p className="field-error">{fieldErrors[`message.body_${lang}`][0]}</p>
              )}
            </div>
            <p className="mt-3 text-caption text-ink-2">{t("notices.message.placeholdersHint")}</p>
            <div className="mt-1 flex flex-wrap gap-1.5">
              {type.placeholders.map((p) => (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => insertToken(p.token)}
                  className="rounded-full border border-line px-2.5 py-0.5 text-caption hover:border-accent"
                  title={p.token}
                >
                  {p.label}
                </button>
              ))}
            </div>
            <p className="mt-3 text-caption text-ink-2">{t("notices.message.privacy")}</p>
          </div>
        )}

        {step === 4 && (
          <div className="space-y-3">
            <label className="flex items-start gap-3">
              <input type="checkbox" checked disabled className="mt-1 h-4 w-4 accent-[var(--pl-accent)]" />
              <span>
                <span className="font-semibold">{t("notices.channels.app")}</span>
                <span className="block text-caption text-ink-2">
                  {t("notices.channels.appHint", {
                    inApp: reach.in_app,
                    push: reach.push,
                    total: selected.length,
                  })}
                </span>
              </span>
            </label>
            <label className="flex items-start gap-3">
              <input
                type="checkbox"
                checked={email}
                onChange={(e) => setEmail(e.target.checked)}
                className="mt-1 h-4 w-4 accent-[var(--pl-accent)]"
              />
              <span>
                <span className="font-semibold">{t("notices.channels.email")}</span>
                <span className="block text-caption text-ink-2">
                  {t("notices.channels.emailHint", { count: reach.email, total: selected.length })}
                </span>
              </span>
            </label>
            {typeKey === "staff_running_late" && (
              <label className="flex items-start gap-3">
                <input
                  type="checkbox"
                  checked={showExpected}
                  onChange={(e) => setShowExpected(e.target.checked)}
                  className="mt-1 h-4 w-4 accent-[var(--pl-accent)]"
                />
                <span>
                  <span className="font-semibold">{t("notices.channels.expected")}</span>
                  <span className="block text-caption text-ink-2">{t("notices.channels.expectedHint")}</span>
                </span>
              </label>
            )}
            {reach.none > 0 && (
              <p className="rounded-lg border border-warning-line bg-warning-wash px-3 py-2 text-dense text-warning-text">
                {t("notices.channels.unreachable", { count: reach.none })}
              </p>
            )}
          </div>
        )}

        {step === 5 && type && (
          <div className="space-y-3">
            <dl className="grid gap-x-6 gap-y-2 sm:grid-cols-2">
              <div>
                <dt className="text-caption text-ink-2">{t("notices.fields.type")}</dt>
                <dd className="font-semibold">{type.label}</dd>
              </div>
              <div>
                <dt className="text-caption text-ink-2">{t("notices.fields.recipients")}</dt>
                <dd className="font-semibold">{t("notices.bookingsCount", { count: selected.length })}</dd>
              </div>
              <div>
                <dt className="text-caption text-ink-2">{t("notices.fields.channels")}</dt>
                <dd>
                  {[t("notices.channels.app"), email ? t("notices.channels.email") : null]
                    .filter(Boolean)
                    .join(" · ")}
                </dd>
              </div>
              <div>
                <dt className="text-caption text-ink-2">{t("notices.fields.bookings")}</dt>
                <dd>{t("notices.confirm.unchanged")}</dd>
              </div>
            </dl>
            {LANGS.map((l) => (
              <div
                key={l}
                dir={l === "ar" ? "rtl" : "ltr"}
                lang={l}
                className="rounded-lg border border-line p-3"
              >
                <p className="text-caption text-ink-2">{t(`notices.lang.${l}`)}</p>
                <p className="font-semibold">{message[`title_${l}`]}</p>
                <p className="whitespace-pre-line text-dense">{message[`body_${l}`]}</p>
              </div>
            ))}
            <p className="text-caption text-ink-2">{t("notices.confirm.hint")}</p>
          </div>
        )}
      </div>

      <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
        <button
          type="button"
          className="btn-secondary h-9 px-4"
          disabled={step === 0 || busy}
          onClick={() => setStep((s) => Math.max(0, s - 1))}
        >
          {t("notices.wizard.back")}
        </button>
        {step === 1 ? (
          <button
            type="button"
            className="btn-primary h-9 px-4"
            disabled={busy || !type}
            onClick={findBookings}
          >
            {busy ? t("notices.wizard.finding") : t("notices.wizard.find")}
          </button>
        ) : step === 5 ? (
          <button
            type="button"
            className="btn-primary h-9 px-4"
            disabled={busy || selected.length === 0}
            onClick={send}
          >
            {busy ? t("notices.wizard.sending") : t("notices.wizard.send", { count: selected.length })}
          </button>
        ) : (
          <button
            type="button"
            className="btn-primary h-9 px-4"
            disabled={!canNext || step === 0}
            onClick={() => setStep((s) => s + 1)}
          >
            {t("notices.wizard.next")}
          </button>
        )}
      </div>
    </div>
  );
}

function ScopeForm({ type, scope, lookups, setField, fieldError }) {
  const { t } = useTranslation();
  const fields = [...type.scope, ...(type.type === "price_change" ? ["branch_ids"] : [])];
  const optional = {
    staff_left: ["date_from"],
    price_change: ["date_from", "branch_ids"],
    opening_hours_change: ["date_to"],
  };
  const isOptional = (field) => (optional[type.type] ?? []).includes(field);
  const label = (field) => (
    <span>
      {t(`notices.scope.${field}`)}
      {isOptional(field) && (
        <span className="ms-1 text-caption text-ink-muted">{t("notices.scope.optional")}</span>
      )}
    </span>
  );

  const toggleIn = (field, id) => {
    const current = scope[field] ?? [];
    setField(field, current.includes(id) ? current.filter((x) => x !== id) : [...current, id]);
  };

  const loadHours = async () => {
    if (!scope.branch_id) return;
    const rows = await fetchWorkingHours(scope.branch_id).catch(() => []);
    setField(
      "hours",
      rows.map((r) => ({
        day: r.day,
        opens_at: (r.opens_at ?? "").slice(0, 5),
        closes_at: (r.closes_at ?? "").slice(0, 5),
      }))
    );
  };

  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <p className="text-dense text-ink-2 sm:col-span-2">{t(`notices.typeHint.${type.type}`)}</p>
      {fields.map((field) => {
        const err = fieldError(field);
        const id = `scope-${field}`;

        if (field === "staff_id") {
          return (
            <div key={field}>
              <label className="field-label" htmlFor={id}>
                {label(field)}
              </label>
              <select
                id={id}
                className="field-input"
                value={scope.staff_id ?? ""}
                onChange={(e) => setField(field, e.target.value)}
              >
                <option value="">{t("notices.scope.choose")}</option>
                {lookups.providers.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.display_name}
                  </option>
                ))}
              </select>
              {err && <p className="field-error">{err}</p>}
            </div>
          );
        }

        if (field === "branch_id" || field === "from_branch_id") {
          return (
            <div key={field}>
              <label className="field-label" htmlFor={id}>
                {label(field)}
              </label>
              <select
                id={id}
                className="field-input"
                value={scope[field] ?? ""}
                onChange={(e) => setField(field, e.target.value)}
              >
                <option value="">{t("notices.scope.choose")}</option>
                {lookups.branches.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </select>
              {err && <p className="field-error">{err}</p>}
            </div>
          );
        }

        if (field === "branch_ids" || field === "service_ids") {
          const items = field === "branch_ids" ? lookups.branches : lookups.services;
          return (
            <fieldset key={field} className="sm:col-span-2">
              <legend className="field-label">{label(field)}</legend>
              <div className="grid max-h-56 gap-1 overflow-y-auto rounded-lg border border-line p-2 sm:grid-cols-2">
                {items.map((item) => (
                  <label
                    key={item.id}
                    className="flex items-center gap-2 rounded px-1 py-0.5 hover:bg-raised"
                  >
                    <input
                      type="checkbox"
                      className="h-4 w-4 accent-[var(--pl-accent)]"
                      checked={(scope[field] ?? []).includes(item.id)}
                      onChange={() => toggleIn(field, item.id)}
                    />
                    <span className="text-dense">
                      {typeof item.name === "string" ? item.name : (item.name?.en ?? item.id)}
                    </span>
                  </label>
                ))}
              </div>
              {err && <p className="field-error">{err}</p>}
            </fieldset>
          );
        }

        if (field === "starts_at" || field === "ends_at") {
          return (
            <div key={field}>
              <label className="field-label" htmlFor={id}>
                {label(field)}
              </label>
              <input
                id={id}
                type="datetime-local"
                className="field-input"
                value={toInputLocal(scope[field])}
                onChange={(e) => setField(field, toApiLocal(e.target.value))}
              />
              {err && <p className="field-error">{err}</p>}
            </div>
          );
        }

        if (field === "delay_minutes") {
          return (
            <div key={field}>
              <label className="field-label" htmlFor={id}>
                {label(field)}
              </label>
              <input
                id={id}
                type="number"
                min={5}
                max={240}
                step={5}
                className="field-input"
                value={scope.delay_minutes ?? ""}
                onChange={(e) => setField(field, e.target.value)}
              />
              {err && <p className="field-error">{err}</p>}
            </div>
          );
        }

        if (field === "hours") {
          return (
            <HoursEditor
              key={field}
              hours={scope.hours ?? []}
              onChange={(rows) => setField("hours", rows)}
              onLoadCurrent={scope.branch_id ? loadHours : null}
              error={err}
            />
          );
        }

        return (
          <div key={field}>
            <label className="field-label" htmlFor={id}>
              {label(field)}
            </label>
            <input
              id={id}
              type="date"
              className="field-input"
              value={scope[field] ?? ""}
              onChange={(e) => setField(field, e.target.value)}
            />
            {err && <p className="field-error">{err}</p>}
          </div>
        );
      })}
    </div>
  );
}

/** The new weekly hours: one interval per day here; an unticked day is closed. */
function HoursEditor({ hours, onChange, onLoadCurrent, error }) {
  const { t } = useTranslation();
  const byDay = (day) => hours.find((h) => Number(h.day) === day);

  const setDay = (day, patch) => {
    const rest = hours.filter((h) => Number(h.day) !== day);
    if (patch === null) return onChange(rest);
    const current = byDay(day) ?? { day, opens_at: "09:00", closes_at: "17:00" };
    return onChange([...rest, { ...current, ...patch, day }].sort((a, b) => a.day - b.day));
  };

  return (
    <fieldset className="sm:col-span-2">
      <div className="mb-1 flex items-center justify-between gap-2">
        <legend className="field-label">{t("notices.scope.hours")}</legend>
        {onLoadCurrent && (
          <button type="button" className="btn-secondary h-8 px-3 text-dense" onClick={onLoadCurrent}>
            {t("notices.scope.loadCurrentHours")}
          </button>
        )}
      </div>
      <div className="divide-y divide-line rounded-lg border border-line">
        {DAYS.map((day) => {
          const row = byDay(day);
          const ranges = hours.filter((h) => Number(h.day) === day);
          return (
            <div key={day} className="flex flex-wrap items-center gap-3 px-3 py-2">
              <label className="flex w-32 items-center gap-2">
                <input
                  type="checkbox"
                  className="h-4 w-4 accent-[var(--pl-accent)]"
                  checked={Boolean(row)}
                  onChange={(e) => setDay(day, e.target.checked ? {} : null)}
                />
                <span className="text-dense">{t(`notices.days.${day}`)}</span>
              </label>
              {ranges.length > 1 ? (
                // A split day (e.g. 09:00–13:00, 16:00–21:00) is kept as is;
                // untick to close it, or tick again for a single range.
                <span className="text-dense" dir="ltr">
                  {ranges.map((r) => `${r.opens_at}–${r.closes_at}`).join(", ")}
                </span>
              ) : row ? (
                <>
                  <input
                    type="time"
                    className="field-input h-8 w-32 py-0"
                    value={row.opens_at}
                    onChange={(e) => setDay(day, { opens_at: e.target.value })}
                    aria-label={t("notices.scope.opensAt")}
                  />
                  <span aria-hidden="true">–</span>
                  <input
                    type="time"
                    className="field-input h-8 w-32 py-0"
                    value={row.closes_at}
                    onChange={(e) => setDay(day, { closes_at: e.target.value })}
                    aria-label={t("notices.scope.closesAt")}
                  />
                </>
              ) : (
                <span className="text-dense text-ink-muted">{t("notices.scope.closed")}</span>
              )}
            </div>
          );
        })}
      </div>
      {error && <p className="field-error">{error}</p>}
    </fieldset>
  );
}

function BookingsStep({ preview, selected, setSelected }) {
  const { t } = useTranslation();
  const { bookings, counts } = preview;

  if (bookings.length === 0) {
    return <p className="p-6 text-center text-ink-2">{t("notices.preview.empty")}</p>;
  }

  return (
    <div>
      <div className="mb-3 flex flex-wrap gap-x-4 gap-y-1 text-dense text-ink-2">
        <span className="font-semibold text-ink">{t("notices.bookingsCount", { count: counts.total })}</span>
        <span>{t("notices.preview.inApp", { count: counts.in_app })}</span>
        <span>{t("notices.preview.push", { count: counts.push })}</span>
        <span>{t("notices.preview.email", { count: counts.email })}</span>
        {counts.unreachable > 0 && (
          <span className="text-warning-text">
            {t("notices.preview.unreachable", { count: counts.unreachable })}
          </span>
        )}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-dense">
          <thead>
            <tr className="border-b border-line text-start text-caption text-ink-2">
              <th className="w-8 py-2">
                <input
                  type="checkbox"
                  className="h-4 w-4 accent-[var(--pl-accent)]"
                  checked={selected.length === bookings.length}
                  onChange={(e) => setSelected(e.target.checked ? bookings.map((b) => b.id) : [])}
                  aria-label={t("notices.preview.selectAll")}
                />
              </th>
              <th className="py-2 text-start">{t("notices.preview.when")}</th>
              <th className="py-2 text-start">{t("notices.preview.client")}</th>
              <th className="py-2 text-start">{t("notices.preview.services")}</th>
              <th className="py-2 text-start">{t("notices.preview.branch")}</th>
              <th className="py-2 text-start">{t("notices.preview.contact")}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {bookings.map((b) => (
              <tr key={b.id} className={selected.includes(b.id) ? "" : "opacity-60"}>
                <td className="py-2">
                  <input
                    type="checkbox"
                    className="h-4 w-4 accent-[var(--pl-accent)]"
                    checked={selected.includes(b.id)}
                    onChange={(e) =>
                      setSelected((s) => (e.target.checked ? [...s, b.id] : s.filter((id) => id !== b.id)))
                    }
                    aria-label={b.reference}
                  />
                </td>
                <td className="py-2">
                  <span dir="ltr">{b.local_starts_at}</span>
                  {b.local_expected_start && (
                    <span className="block text-caption text-ink-2">
                      {t("notices.preview.expected", { time: b.local_expected_start })}
                    </span>
                  )}
                  <span className="block text-caption text-ink-muted">#{b.reference}</span>
                </td>
                <td className="py-2">
                  {b.client?.name ?? "—"}
                  {b.already_notified && (
                    <span className="ms-1 rounded-full bg-info-wash px-2 py-0.5 text-caption text-info-text">
                      {t("notices.preview.alreadyNotified")}
                    </span>
                  )}
                </td>
                <td className="py-2">
                  {b.services.join(", ")}
                  {b.staff.length > 0 && (
                    <span className="block text-caption text-ink-2">{b.staff.join(", ")}</span>
                  )}
                </td>
                <td className="py-2">{b.branch?.name}</td>
                <td className="py-2">
                  <div className="flex flex-wrap gap-1">
                    {b.contact.in_app && <Chip>{t("notices.contact.app")}</Chip>}
                    {b.contact.push && <Chip>{t("notices.contact.push")}</Chip>}
                    {b.contact.email && <Chip>{t("notices.contact.email")}</Chip>}
                    {!b.reachable && <Chip warn>{t("notices.contact.none")}</Chip>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Chip({ children, warn = false }) {
  return (
    <span
      className={`rounded-full px-2 py-0.5 text-caption ${warn ? "bg-warning-wash text-warning-text" : "bg-raised text-ink-2"}`}
    >
      {children}
    </span>
  );
}
